import React, { useEffect, useState } from 'react';
import { Box, Grid, InputAdornment, Slider, Stack, TextField, Typography } from '@mui/material';
import { CATEGORIES, categoryColor } from './indicatorConfig';

const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(value)));

const FIVE_PERCENT_MARKS = Array.from({ length: 21 }, (_, index) => ({ value: index * 5 }));
const DETENT_RADIUS = 1;

const withFivePercentClicks = (raw) => {
  const nearest = Math.round(raw / 5) * 5;
  return Math.abs(raw - nearest) <= DETENT_RADIUS ? nearest : raw;
};

const WeightRow = ({ indicator, percent, color, onChange }) => {
  const [draft, setDraft] = useState(String(percent));

  useEffect(() => {
    setDraft(String(percent));
  }, [percent]);

  const commit = () => {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(String(percent));
      return;
    }
    const next = clampPercent(parsed);
    setDraft(String(next));
    if (next !== percent) onChange(next / 100);
  };

  return (
    <Box sx={{ mb: 0.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ pr: 1, fontSize: '0.82rem' }}>
          {indicator.label}
          {indicator.placeholder ? ' (TBD)' : ''}
        </Typography>
        <TextField
          size="small"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.target.blur();
            }
          }}
          inputProps={{
            inputMode: 'numeric',
            'aria-label': `${indicator.label} weight percent`,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0.25 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  %
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            width: 72,
            flexShrink: 0,
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.75,
              textAlign: 'right',
              fontSize: '0.82rem',
              fontWeight: 600,
            },
          }}
        />
      </Stack>
      <Slider
        size="small"
        min={0}
        max={100}
        step={1}
        marks={FIVE_PERCENT_MARKS}
        value={percent}
        onChange={(_, value) => {
          const raw = Array.isArray(value) ? value[0] : value;
          onChange(withFivePercentClicks(raw) / 100);
        }}
        aria-label={`${indicator.label} weight`}
        sx={{
          mt: -0.25,
          mb: 0.25,
          color,
          '& .MuiSlider-thumb': { width: 12, height: 12 },
          '& .MuiSlider-mark': {
            width: 1.5,
            height: 6,
            borderRadius: 0.5,
            backgroundColor: 'currentColor',
            opacity: 0.28,
          },
          '& .MuiSlider-markActive': {
            opacity: 0.5,
            backgroundColor: 'currentColor',
          },
        }}
      />
    </Box>
  );
};

const WeightPanel = ({ indicators, weights, onChange, categories = CATEGORIES }) => (
  <Grid container spacing={2.5}>
    {categories.map((category) => {
      const categoryIndicators = indicators.filter(
        (indicator) => indicator.category === category.id
      );
      if (!categoryIndicators.length) return null;
      const color = category.color || categoryColor(category.id);

      return (
        <Grid item xs={12} sm={6} md={4} key={category.id}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <Box sx={{ width: 3, height: 14, bgcolor: color, borderRadius: 1 }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'text.secondary',
                fontSize: '0.65rem',
              }}
            >
              {category.label}
            </Typography>
          </Stack>

          {categoryIndicators.map((indicator) => (
            <WeightRow
              key={indicator.id}
              indicator={indicator}
              percent={Math.round((Number(weights[indicator.id]) || 0) * 100)}
              color={color}
              onChange={(value) => onChange(indicator.id, value)}
            />
          ))}
        </Grid>
      );
    })}
  </Grid>
);

export default WeightPanel;
